import { useState, useEffect, useContext, useCallback } from 'react';
import { db, auth } from '../firebase';
import Footer from '../components/Footer';
import {
  collection,
  query,
  where,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
  getDocs,
} from 'firebase/firestore';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { useMyPedalsChrome } from '../contexts/MyPedalsChromeContext';
import { useRegisterPullToRefresh } from '../hooks/useRegisterPullToRefresh';


const COMPACT_FILTER_SELECT_STYLE = {
  borderRadius: '9999px',
  fontSize: '0.875rem',
  fontFamily: 'Nunito, sans-serif',
  fontWeight: 700,
  backgroundColor: 'white',
  color: '#374151',
  cursor: 'pointer',
  border: '1px solid #C89F3C',
  padding: '0.6rem 1.25rem 0.6rem 0.55rem',
  outline: 'none',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23C89F3C' d='M6 8L0 0h12z'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0.65rem center',
  boxSizing: 'border-box',
};

const WANT_FILTER_SELECT_STYLE = {
  ...COMPACT_FILTER_SELECT_STYLE,
  borderRadius: 0,
  fontSize: '0.9375rem',
  fontWeight: 400,
  width: '100%',
  flex: 1,
  minWidth: 0,
  border: 'none',
  padding: '0.15rem 2rem 0.15rem 0',
  backgroundColor: 'transparent',
  boxShadow: 'none',
  backgroundPosition: 'right 0.25rem center',
};

const WANT_FILTER_FIELD_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.65rem',
  width: '100%',
  borderRadius: '25px',
  border: '1px solid #C89F3C',
  backgroundColor: 'white',
  padding: '0.65rem 1rem 0.65rem 1.1rem',
  boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
  boxSizing: 'border-box',
};

const WANT_FILTER_LABEL_STYLE = {
  fontSize: '0.9rem',
  fontWeight: 600,
  color: '#4A5759',
  fontFamily: 'Nunito, sans-serif',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const ADD_WANT_LINK_STYLE = {
  backgroundColor: 'transparent',
  border: 'none',
  padding: 0,
  fontFamily: 'Nunito, sans-serif',
  color: '#C9A227',
  fontWeight: 'bold',
  fontSize: '19px',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const WantList = () => {
  const { firestoreUser } = useContext(UserContext);
  const location = useLocation();
  const myPedalsChrome = useMyPedalsChrome();
  const isHubView = location.pathname.includes('/my-pedals');
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const isFullyVerified = currentUser?.emailVerified && firestoreUser?.phoneVerified && firestoreUser?.isVerified;
  
  
  // 🔍 DEBUG: Log verification calculation
  console.log('🔍 WANTLIST VERIFICATION CALCULATION:', {
    currentUserEmailVerified: currentUser?.emailVerified,
    firestorePhoneVerified: firestoreUser?.phoneVerified,
    firestoreIsVerified: firestoreUser?.isVerified,
    isFullyVerified: isFullyVerified,
    calculation: `${currentUser?.emailVerified} && ${firestoreUser?.phoneVerified} && ${firestoreUser?.isVerified} = ${isFullyVerified}`
  });

  const navigate = useNavigate();

  const [wants, setWants] = useState([]);
  const [wantsLoading, setWantsLoading] = useState(true);
  const [filters, setFilters] = useState({
    make: '',
    model: '',
    maxPrice: '',
    condition: '',
    decade: '',
    effectType: ''
  });
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableEffectTypes, setAvailableEffectTypes] = useState([]);
  const [showBackToTop, setShowBackToTop] = useState(false);

// Show/hide "Back to Top" button based on scroll position
useEffect(() => {
  const handleScroll = () => {
    if (window.scrollY > 300) {
      setShowBackToTop(true);
    } else {
      setShowBackToTop(false);
    }
  };

  window.addEventListener('scroll', handleScroll);
  return () => window.removeEventListener('scroll', handleScroll);
}, []);

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// Track auth state
useEffect(() => {
  const unsubscribe = auth.onAuthStateChanged((user) => {
    setCurrentUser(user);
    setAuthLoading(false);
  });

  return () => unsubscribe();
}, []);

useEffect(() => {
  if (!isHubView || !myPedalsChrome) return undefined;

  myPedalsChrome.setRightSlot(
    <select
      className="my-pedals-compact-filter-select"
      value={filters.make}
      onChange={(e) => setFilters({ ...filters, make: e.target.value })}
      aria-label="Filter wants by brand"
      style={COMPACT_FILTER_SELECT_STYLE}
    >
      <option value="">All Brands</option>
      {availableMakes.map((make) => (
        <option key={make} value={make}>{make}</option>
      ))}
    </select>
  );

  return () => myPedalsChrome.setRightSlot(null);
// eslint-disable-next-line react-hooks/exhaustive-deps -- setRightSlot is stable
}, [isHubView, myPedalsChrome?.setRightSlot, filters.make, availableMakes]);

// Desktop hub only: render the "+ Add New Want" button inline with the header
// toggle (right-aligned). The header row it lands in is hidden on mobile, so
// this never affects the mobile layout. Unverified users still see the in-page
// verification prompt below the toggle.
useEffect(() => {
  if (!isHubView || !myPedalsChrome) return undefined;

  if (isFullyVerified) {
    myPedalsChrome.setAddSlot(
      <Link
        to='/addpedal'
        state={{ tab: 'want' }}
        className='inline-block'
        style={ADD_WANT_LINK_STYLE}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.75'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
      >
        + Add New Want
      </Link>
    );
  } else {
    myPedalsChrome.setAddSlot(null);
  }

  return () => myPedalsChrome.setAddSlot(null);
// eslint-disable-next-line react-hooks/exhaustive-deps -- setAddSlot is stable
}, [isHubView, myPedalsChrome?.setAddSlot, isFullyVerified]);

const toggleNotification = async (wantId, currentValue) => {
  try {
    const wantRef = doc(db, 'wantList', wantId);
    await updateDoc(wantRef, {
      notifyOnMatch: !currentValue
    });
    console.log(`Updated notifyOnMatch to ${!currentValue} for ${wantId}`);
  } catch (error) {
    console.error('Failed to update notification setting:', error);
  }
};

  useEffect(() => {
    let unsubscribe = null;

    const setupWantsListener = (user) => {
      if (!user) {
        setWants([]);
        setWantsLoading(false);
        setAvailableMakes([]);
        setAvailableModels([]);
        return;
      }

      // Reset loading state when setting up a new listener
      setWantsLoading(true);

      const q = query(collection(db, 'wantList'), where('uid', '==', user.uid));

      unsubscribe = onSnapshot(q, (snapshot) => {
        const wantsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setWants(wantsData);
        
        // Normalize brand names to avoid duplicates (e.g., "electro-harmonix" vs "Electro-Harmonix")
        const normalizeBrandName = (brand) => {
          if (!brand) return '';
          return brand.toLowerCase().trim();
        };

        const brandMap = new Map();
        wantsData.forEach(w => {
          if (w.make) {
            const normalized = normalizeBrandName(w.make);
            if (normalized && !brandMap.has(normalized)) {
              // Store the original capitalization for display
              brandMap.set(normalized, w.make);
            }
          }
        });

        const uniqueMakes = Array.from(brandMap.values()).sort();
        const uniqueModels = [...new Set(wantsData.map(w => w.model).filter(Boolean))];
        
        // Get all unique effect types with counts
        const effectTypeCounts = {};
        wantsData.forEach(w => {
          if (w.effectTypes && Array.isArray(w.effectTypes)) {
            w.effectTypes.forEach(type => {
              effectTypeCounts[type] = (effectTypeCounts[type] || 0) + 1;
            });
          }
        });
        const uniqueEffectTypes = Object.entries(effectTypeCounts)
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([type, count]) => ({ type, count }));
        
        setAvailableMakes(uniqueMakes);
        setAvailableModels(uniqueModels);
        setAvailableEffectTypes(uniqueEffectTypes);
        setWantsLoading(false);
      });
    };

    // Set up listener for current user
    setupWantsListener(auth.currentUser);

    // Also listen for auth state changes to handle refresh scenarios
    const authUnsubscribe = auth.onAuthStateChanged((user) => {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
      setupWantsListener(user);
    });

    return () => {
      if (unsubscribe) unsubscribe();
      authUnsubscribe();
    };
  }, []);

  const refreshWants = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    setWantsLoading(true);
    try {
      const q = query(collection(db, 'wantList'), where('uid', '==', user.uid));
      const snapshot = await getDocs(q);
      const wantsData = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setWants(wantsData);

      const normalizeBrandName = (brand) => {
        if (!brand) return '';
        return brand.toLowerCase().trim();
      };
      const brandMap = new Map();
      wantsData.forEach((w) => {
        if (w.make) {
          const normalized = normalizeBrandName(w.make);
          if (normalized && !brandMap.has(normalized)) {
            brandMap.set(normalized, w.make);
          }
        }
      });
      const uniqueMakes = Array.from(brandMap.values()).sort();
      const uniqueModels = [...new Set(wantsData.map((w) => w.model).filter(Boolean))];
      const effectTypeCounts = {};
      wantsData.forEach((w) => {
        if (w.effectTypes && Array.isArray(w.effectTypes)) {
          w.effectTypes.forEach((type) => {
            effectTypeCounts[type] = (effectTypeCounts[type] || 0) + 1;
          });
        }
      });
      const uniqueEffectTypes = Object.entries(effectTypeCounts)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([type, count]) => ({ type, count }));
      setAvailableMakes(uniqueMakes);
      setAvailableModels(uniqueModels);
      setAvailableEffectTypes(uniqueEffectTypes);
    } finally {
      setWantsLoading(false);
    }
  }, []);

  useRegisterPullToRefresh(refreshWants);

  const handleDelete = async (id) => {
    // Get the want to show details in confirmation
    const wantToDelete = wants.find(w => w.id === id);
    if (!wantToDelete) {
      console.error('handleDelete: want not found', id);
      return;
    }

    // Build a description for the confirmation message
    let description = '';
    if (wantToDelete.make && wantToDelete.model) {
      description = `${wantToDelete.make} ${wantToDelete.model}`;
    } else if (wantToDelete.effectTypes && wantToDelete.effectTypes.length > 0) {
      description = wantToDelete.effectTypes.join(', ');
    } else {
      description = 'this want';
    }
    
    // Show confirmation dialog
    const confirmed = window.confirm(`Are you sure you want to delete ${description} from your Wantlist?`);
    
    if (!confirmed) {
      return; // User cancelled
    }
    
    try {
      await deleteDoc(doc(db, 'wantList', id));
      
      // Update wants state
      const updatedWants = wants.filter((w) => w.id !== id);
      setWants(updatedWants);
      
      // Check if the deleted want was the last one matching the current filters
      // If so, reset the filters to show all remaining wants
      const remainingFiltered = applyFilters(updatedWants);
      if (remainingFiltered.length === 0 && updatedWants.length > 0) {
        // Reset filters to show all wants
        setFilters({
          make: '',
          model: '',
          maxPrice: '',
          condition: '',
          decade: '',
          effectType: ''
        });
      }
    } catch (err) {
      console.error('Error deleting want:', err);
    }
  };

  const handleEdit = (want) => {
    // Edit happens on the standalone Add Want screen so the form has its own
    // focused surface (mirrors the Add Pedal flow). On save it shows a
    // success card; the user can then return to Wantlist or add another.
    navigate(`/addpedal?tab=want&edit=${encodeURIComponent(want.id)}`, {
      state: { tab: 'want' }
    });
  };

  const applyFilters = (items) => {
  return items.filter((item) => {
    // Check if item matches the effect type filter
    const matchesEffectType = !filters.effectType || 
      (item.effectTypes && Array.isArray(item.effectTypes) && item.effectTypes.includes(filters.effectType));
    
    return (
      (!filters.make || item.make?.toLowerCase().includes(filters.make.toLowerCase())) &&
      (!filters.model || item.model?.toLowerCase().includes(filters.model.toLowerCase())) &&
      (!filters.maxPrice || (!isNaN(item.maxPrice) && parseFloat(item.maxPrice) <= parseFloat(filters.maxPrice))) &&
      (!filters.condition || item.condition === filters.condition) &&
      (!filters.decade || item.decade === filters.decade) &&
      matchesEffectType
    );
  });
};


  if (!authLoading && !currentUser) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }}>
        <style>{`
          @keyframes modalPulse {
            0%, 100% { box-shadow: 0 8px 32px rgba(90, 158, 203, 0.3); }
            50% { box-shadow: 0 8px 48px rgba(90, 158, 203, 0.6); }
          }
          @media (max-width: 768px) {
            .preview-modal {
              max-width: 240px !important;
              padding: 1rem 1rem !important;
            }
            .preview-modal h2 { font-size: 1.05rem !important; }
            .preview-modal p { font-size: 0.8rem !important; margin-bottom: 0.75rem !important; }
            .preview-modal a.preview-signup-btn { font-size: 14px !important; padding: 8px 24px !important; }
            .preview-modal p.preview-modal-login { font-size: 0.8rem !important; margin: 0.25rem 0 0 0 !important; }
          }
        `}</style>
        <picture>
          <source media="(max-width: 768px)" srcSet="/images/wantlist-preview-mobile.png?v=2" />
          <img
            src="/images/wantlist-preview-desktop.png?v=2"
            alt="Wantlist preview"
            style={{ width: '100%', display: 'block' }}
          />
        </picture>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          pointerEvents: 'none'
        }} />
        <div className="preview-modal" style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 100,
          backgroundColor: 'rgba(255, 255, 255, 0.80)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderRadius: '16px',
          padding: '2.5rem 2rem',
          maxWidth: '420px',
          width: '90%',
          textAlign: 'center',
          fontFamily: 'Nunito, sans-serif',
          animation: 'modalPulse 3s ease-in-out infinite',
          border: '2px solid rgba(224, 224, 224, 0.7)'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#374151', marginBottom: '0.75rem' }}>
            Build Your Wantlist
          </h2>
          <p style={{ fontSize: '1rem', color: '#6B7280', marginBottom: '1.5rem', lineHeight: '1.5' }}>
            Sign up to save the pedals and effect types you're looking for. We'll tell you when one is listed.
          </p>
          <Link className="preview-signup-btn" to="/signup" style={{
            display: 'inline-block',
            backgroundColor: '#C9A227',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '16px',
            padding: '12px 32px',
            borderRadius: '9999px',
            textDecoration: 'none',
            fontFamily: 'Nunito, sans-serif',
            marginBottom: '1rem'
          }}>
            Sign Up Free
          </Link>
          <p className="preview-modal-login" style={{ fontSize: '0.9rem', color: '#6B7280' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#5A9ECB', fontWeight: 'bold', textDecoration: 'none' }}>
              Log In
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'transparent', minHeight: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Yellow background section */}
      <div style={{ backgroundColor: '#F7F3EB', flex: 1, padding: '0 1rem' }}>

      {isHubView && (
        <style>{`
          @media (min-width: 769px) {
            .wantlist-hub-add-cta {
              padding-top: 0.25rem !important;
              margin-bottom: 2rem !important;
              position: relative;
              z-index: 2;
            }
            .wantlist-hub-layout {
              max-width: 1100px !important;
              padding: 0 2rem !important;
              box-sizing: border-box !important;
              margin-top: 0 !important;
            }
          }
          @media (max-width: 768px) {
            .wantlist-hub-expanded-header {
              display: none !important;
            }
            .wantlist-hub-filter-section {
              display: none !important;
            }
          }
        `}</style>
      )}

      {/* Add New Want CTA — standalone /wantlist only. In the hub view the
          verified button is rendered inline with the header toggle (see
          setAddSlot); unverified users still see the verification prompt below. */}
      {isFullyVerified ? (
        !isHubView ? (
        <div style={{ textAlign: 'center', paddingTop: '1rem', marginBottom: '0.5rem' }}>
          <Link
            to='/addpedal'
            state={{ tab: 'want' }}
            className='inline-block'
            style={ADD_WANT_LINK_STYLE}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.75'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            + Add New Want
          </Link>
        </div>
        ) : null
      ) : (
        <div className={isHubView ? 'wantlist-hub-expanded-header p-4 border border-orange-300 rounded-lg bg-orange-50' : 'p-4 border border-orange-300 rounded-lg bg-orange-50'} style={{ maxWidth: '600px', margin: '1rem auto' }}>
          <h3 className='text-lg font-semibold text-orange-800 mb-2'>⚠️ Account Verification Required</h3>
          <p className='text-orange-700 text-sm mb-3'>
            You must complete both email and phone verification before adding items to your Wantlist.
          </p>
          <Link to='/profile' className='inline-block bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded text-sm font-medium'>
            Complete Verification
          </Link>
        </div>
      )}

      {!isHubView && (
        <>
          <h1 className="text-2xl font-bold text-center" style={{ padding: '1rem 1rem 0.5rem 1rem' }}>My Wantlist</h1>
          <p className="text-gray-600 text-center mb-6" style={{ padding: '0 1.5rem', fontSize: '16px', maxWidth: '800px', margin: '0 auto 1.5rem auto' }}>
            Add a pedal or effect type you're looking for. We'll notify you when one is listed.
          </p>
        </>
      )}

      {/* Single-column layout: optional inline form (edit/prefill) + the
          Filter & Saved Wants card. */}
      <div 
        className={['want-list-layout', isHubView && 'wantlist-hub-layout'].filter(Boolean).join(' ')}
        style={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: '2rem', 
          maxWidth: '1000px', 
          margin: '0 auto', 
          padding: '0 1rem'
        }}
      >
        <style jsx>{`
          @media (max-width: 768px) {
            .want-list-layout {
              flex-direction: column !important;
              gap: 1rem !important;
              padding: 0 0.5rem !important;
            }
          }
        `}</style>
        
        {/* Filters sit on the page background; white card wraps Saved Wants only */}
        <h2 className={isHubView ? 'wantlist-hub-filter-section' : undefined} style={{
          marginTop: '1rem',
          color: '#4A5759',
          fontFamily: 'Nunito',
          fontSize: '1.25rem',
          fontWeight: '600',
          marginBottom: '0.5rem',
        }}>Filter Wants</h2>
        <div
          className={['want-list-filters', isHubView && 'wantlist-hub-filter-section'].filter(Boolean).join(' ')}
          style={{ marginBottom: '1.5rem', marginTop: '0.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', columnGap: '1.25rem', rowGap: '1rem', position: 'relative' }}
        >
          <style jsx>{`
            @media (max-width: 768px) {
              .want-list-filters {
                grid-template-columns: 1fr !important;
                column-gap: 0 !important;
                row-gap: 0.15rem !important;
                padding: 0 !important;
                margin-top: 0.75rem !important;
              }
              .want-list-filters label,
              .want-list-filters .want-filter-label {
                font-weight: 600 !important;
                font-size: 0.9rem !important;
              }
              .want-list-filters .want-filter-field {
                width: 100% !important;
                box-sizing: border-box !important;
              }
              .want-list-filters select {
                width: 100% !important;
                margin-right: 0 !important;
                box-sizing: border-box !important;
              }
            }
          `}</style>
          <div style={WANT_FILTER_FIELD_STYLE} className="want-filter-field">
            <span className="want-filter-label" style={WANT_FILTER_LABEL_STYLE}>Brand</span>
            <select
              value={filters.make}
              onChange={(e) => {
                setFilters({ ...filters, make: e.target.value });
                e.target.blur();
              }}
              style={WANT_FILTER_SELECT_STYLE}
            >
              <option value='' />
              {availableMakes.map((make, idx) => (
                <option key={idx} value={make}>{make}</option>
              ))}
            </select>
          </div>
          <div style={WANT_FILTER_FIELD_STYLE} className="want-filter-field">
            <span className="want-filter-label" style={WANT_FILTER_LABEL_STYLE}>Model</span>
            <select
              value={filters.model}
              onChange={(e) => {
                setFilters({ ...filters, model: e.target.value });
                e.target.blur();
              }}
              style={WANT_FILTER_SELECT_STYLE}
            >
              <option value='' />
              {availableModels.map((model, idx) => (
                <option key={idx} value={model}>{model}</option>
              ))}
            </select>
          </div>
          <div style={WANT_FILTER_FIELD_STYLE} className="want-filter-field">
            <span className="want-filter-label" style={WANT_FILTER_LABEL_STYLE}>Effect Type</span>
            <select
              value={filters.effectType}
              onChange={(e) => setFilters({ ...filters, effectType: e.target.value })}
              style={WANT_FILTER_SELECT_STYLE}
            >
              <option value='' />
              {availableEffectTypes.map((item, idx) => (
                <option key={idx} value={item.type}>{item.type} ({item.count})</option>
              ))}
            </select>
          </div>
        </div>

        <div
          className="want-list-right-column"
          style={{
            borderRadius: '12px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            backgroundColor: 'white',
            padding: '2rem',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
          }}
        >
          <style jsx>{`
            @media (max-width: 768px) {
              .want-list-right-column {
                min-width: unset !important;
                padding: 1rem !important;
              }
            }
            @media (min-width: 769px) {
              .want-card-actions {
                justify-content: flex-start !important;
              }
              .want-card-edit-btn {
                flex: 0 0 auto !important;
                width: 150px !important;
              }
              .want-card-delete-btn {
                flex: 0 0 auto !important;
                width: 120px !important;
                padding-left: 0 !important;
                padding-right: 0 !important;
              }
            }
          `}</style>

          {/* Saved Wants Section */}
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '0.5rem',
            marginTop: 0,
            fontFamily: 'inherit',
          }}>Saved Wants</h3>
          {wantsLoading ? (
            // Swiping in from the pedalboard mounts this before the snapshot
            // lands, so the empty state has to wait for the load to finish or it
            // tells the user they have no wants when they do.
            <p>Loading your wants…</p>
          ) : wants.length === 0 ? (
            <p>No wants saved yet.</p>
          ) : (
            <div className='space-y-6'>
              {applyFilters(wants).map((want) => (
                <div
                  key={want.id}
                  style={{ marginBottom: '20px', padding: '20px', border: '1px solid #d1d5db', borderRadius: '12px', backgroundColor: '#fafafa', position: 'relative' }}
                >
                  {/* Header row: title/criteria heading on the left, Match Notifications box
                      hugging the top-right corner. Flex layout keeps content from ever sliding
                      under the box, regardless of how much title content a want has. */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Make / Model */}
                      {(want.make || want.model) && (
                        <div style={{ marginBottom: '18px', textAlign: 'left' }}>
                          {want.make && (
                            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1f2937' }}>
                              {want.make}
                            </div>
                          )}
                          {want.model && (
                            <div style={{ fontSize: '1rem', fontWeight: '600', color: '#6b7280' }}>
                              {want.model}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Show "General Want" if neither make/model nor effectTypes are specified */}
                      {(!want.make && !want.model) && (!want.effectTypes || want.effectTypes.length === 0) && (
                        <div style={{ fontSize: '1rem', fontWeight: '600', color: '#6b7280', marginBottom: '10px' }}>
                          General Want
                        </div>
                      )}
                    </div>

                    {/* Match Notifications - self-contained white box, label + toggle centered */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      width: '90px',
                      boxSizing: 'border-box',
                      flexShrink: 0,
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '10px',
                      padding: '8px 6px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                      marginTop: '-14px',
                      marginRight: '-14px'
                    }}>
                      <span style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', textAlign: 'center', lineHeight: 1.15 }}>Match Notifications</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={want.notifyOnMatch ?? false}
                        aria-label="Match notifications"
                        onClick={() => toggleNotification(want.id, want.notifyOnMatch ?? false)}
                        style={{
                          width: '44px',
                          height: '24px',
                          borderRadius: '12px',
                          border: 'none',
                          backgroundColor: (want.notifyOnMatch ?? false) ? '#C9A227' : '#d1d5db',
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                          flexShrink: 0,
                          padding: 0
                        }}
                      >
                        <span style={{
                          position: 'absolute',
                          top: '2px',
                          left: (want.notifyOnMatch ?? false) ? '22px' : '2px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          transition: 'left 0.2s ease',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }} />
                      </button>
                    </div>
                  </div>

                  {/* Pill badges row */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                    {/* Effect type pills */}
                    {want.effectTypes && want.effectTypes.length > 0 && want.effectTypes.map((type) => (
                      <span
                        key={type}
                        style={{
                          display: 'inline-block',
                          padding: '4px 14px',
                          borderRadius: '9999px',
                          backgroundColor: 'rgba(201, 162, 39, 0.12)',
                          color: '#946F00',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          fontFamily: 'Nunito, sans-serif'
                        }}
                      >
                        {type}
                      </span>
                    ))}

                    {/* Max Price pill */}
                    {want.maxPrice && (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 14px',
                        borderRadius: '9999px',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        fontFamily: 'Nunito, sans-serif',
                        border: '1px solid #e5e7eb'
                      }}>
                        Max ${want.maxPrice}
                      </span>
                    )}

                    {/* Condition pill */}
                    {want.condition && (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 14px',
                        borderRadius: '9999px',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        fontFamily: 'Nunito, sans-serif',
                        border: '1px solid #e5e7eb'
                      }}>
                        {want.condition}+ condition
                      </span>
                    )}

                    {/* Decade pill */}
                    {want.decade && (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 14px',
                        borderRadius: '9999px',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        fontFamily: 'Nunito, sans-serif',
                        border: '1px solid #e5e7eb'
                      }}>
                        {want.decade}
                      </span>
                    )}

                    {/* Color pill */}
                    {want.color && (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 14px',
                        borderRadius: '9999px',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        fontFamily: 'Nunito, sans-serif',
                        border: '1px solid #e5e7eb'
                      }}>
                        {want.color}
                      </span>
                    )}

                    {/* Excluded keyword pills. Red rather than the neutral grey
                        of the criteria pills above, because these mean the
                        opposite — reading one as something the user wants would
                        be an easy and confusing mistake. */}
                    {Array.isArray(want.excludedKeywords) && want.excludedKeywords.map((keyword) => (
                      <span key={`exclude-${keyword}`} style={{
                        display: 'inline-block',
                        padding: '4px 14px',
                        borderRadius: '9999px',
                        backgroundColor: '#fef2f2',
                        color: '#b91c1c',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        fontFamily: 'Nunito, sans-serif',
                        border: '1px solid #fecaca'
                      }}>
                        No {keyword}
                      </span>
                    ))}
                  </div>

                  {want.description && (
                    <div style={{ marginTop: '12px', fontSize: '0.85rem', color: '#6b7280', fontStyle: 'italic' }}>
                      {want.description}
                    </div>
                  )}

                  {/* Edit / Delete buttons */}
                  <div className="want-card-actions" style={{ marginTop: '16px', display: 'flex', gap: '12px', justifyContent: 'center', borderTop: '1px solid #e5e7eb', paddingTop: '14px' }}>
                    <button
                      type="button"
                      onClick={() => handleEdit(want)}
                      className="want-card-edit-btn"
                      style={{
                        backgroundColor: '#C9A227',
                        borderRadius: '9999px',
                        border: 'none',
                        padding: '8px 0',
                        fontFamily: 'Nunito, sans-serif',
                        fontWeight: '700',
                        fontSize: '0.95rem',
                        color: 'white',
                        cursor: 'pointer',
                        flex: '1',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 3px 8px rgba(201, 162, 39, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(want.id)}
                      className="want-card-delete-btn"
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '9999px',
                        border: '1px solid #d1d5db',
                        padding: '8px 24px',
                        fontFamily: 'Nunito, sans-serif',
                        fontWeight: '700',
                        fontSize: '0.95rem',
                        color: '#374151',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 3px 8px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>

      {/* Fixed gap below content */}
      <div style={{ height: '200px' }}></div>
      </div>
      
      {/* Footer */}
      <Footer />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="back-to-top-btn"
          style={{
            position: 'fixed',
            bottom: window.innerWidth <= 768 ? '90px' : '30px',
            right: '30px',
            backgroundColor: '#5A9ECB',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#4A8AB0';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#5A9ECB';
            e.target.style.transform = 'scale(1)';
          }}
          aria-label="Back to top"
        >
          ⌃
        </button>
      )}
    </div>
  );
};

export default WantList;
